import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FileText, Clock, CheckCircle, XCircle, Send, Search, Trash2, Copy, Download, GitBranch,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-50 text-blue-600 border-blue-200', icon: Send },
  accepted: { label: 'Accepted', color: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-600 border-red-200', icon: XCircle },
};

// Display label for the estimate_type stored on the doc (matches template
// categories). Unknown values fall back to title-cased raw value.
const TYPE_LABELS = {
  kitchen: 'Kitchen',
  bathroom: 'Bathroom',
  roofing: 'Roofing',
  deck: 'Deck',
  painting: 'Painting',
  general: 'General',
  garage_door: 'Garage Door',
  flooring: 'Flooring',
  basement: 'Basement',
  windows: 'Windows',
  demolition: 'Demolition',
  drywall: 'Drywall',
  framing: 'Framing',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  custom: 'Custom',
};

const typeLabel = (t) => {
  if (!t) return '—';
  return TYPE_LABELS[t] || t.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Columns available for sorting. `key` is the accessor name, `label`
// shows in the dropdown, `get` extracts the comparable primitive.
const SORT_COLUMNS = [
  { key: 'created_at',     label: 'Created',        get: (e) => e.created_at || '' },
  { key: 'updated_at',     label: 'Last Edited',    get: (e) => e.updated_at || '' },
  { key: 'title',          label: 'Job',            get: (e) => (e.title || '').toLowerCase() },
  { key: 'client',         label: 'Client',         get: (e) => clientLastFirst(e.client).toLowerCase() },
  { key: 'estimate_type',  label: 'Type',           get: (e) => typeLabel(e.estimate_type).toLowerCase() },
  { key: 'estimate_number',label: 'Estimate #',     get: (e) => e.estimate_number || '' },
  { key: 'total',          label: 'Total',          get: (e) => calcTotal(e) },
  { key: 'status',         label: 'Status',         get: (e) => e.status || '' },
];

// Format a client name as "Last, First" for display/sorting. Falls back
// to company, or the raw single-word name.
function clientLastFirst(client) {
  const company = (client?.company || '').trim();
  if (company) return company;
  const name = (client?.name || '').trim();
  if (!name) return '';
  const parts = name.split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  const first = parts.slice(0, -1).join(' ');
  return `${last}, ${first}`;
}

// Reusable total calculator (subtotal + GST + QST)
function calcTotal(est) {
  let sub = 0;
  (est.sections || []).forEach(s => {
    (s.items || []).forEach(item => { sub += (item.qty || 0) * (item.unit_price || 0); });
  });
  const gst = sub * (est.tax_gst || 5) / 100;
  const qst = sub * (est.tax_qst || 9.975) / 100;
  return sub + gst + qst;
}

// Flatten the estimate to a single searchable string. Expands to every
// text field the user could plausibly remember — address, phone, notes,
// section titles, line items, contingencies, etc.
function searchHaystack(est) {
  const parts = [
    est.title, est.estimate_number, est.status, est.payment_terms, est.notes,
    typeLabel(est.estimate_type),
    est.client?.name, est.client?.company, est.client?.email, est.client?.phone,
    est.client?.address1, est.client?.address2, est.client?.postal_code,
  ];
  (est.project_bullets || []).forEach(b => parts.push(b));
  (est.general_provisions || []).forEach(p => parts.push(p));
  (est.sections || []).forEach(s => {
    parts.push(s.title);
    (s.items || []).forEach(it => parts.push(it.description));
    (s.contingencies || []).forEach(c => parts.push(c));
  });
  return parts.filter(Boolean).join(' \u00b7 ').toLowerCase();
}

export default function EstimatesList() {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [coCounts, setCoCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchEstimates(); fetchCoCounts(); }, []);

  // Refetch when the user comes back to this tab/window — keeps the
  // status column fresh after edits made elsewhere (or in another tab).
  useEffect(() => {
    const onFocus = () => { fetchEstimates(); fetchCoCounts(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const fetchEstimates = async () => {
    try {
      const res = await fetch(`${API}/estimates`, { credentials: 'include' });
      if (res.ok) setEstimates(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoCounts = async () => {
    try {
      const res = await fetch(`${API}/change-orders`, { credentials: 'include' });
      if (res.ok) {
        const cos = await res.json();
        const counts = {};
        cos.forEach(co => {
          counts[co.parent_estimate_id] = (counts[co.parent_estimate_id] || 0) + 1;
        });
        setCoCounts(counts);
      }
    } catch { /* ignore */ }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await fetch(`${API}/estimates/${deleteId}`, { method: 'DELETE', credentials: 'include' });
      setEstimates(prev => prev.filter(e => e.estimate_id !== deleteId));
      toast.success('Estimate deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
    setDeleteId(null);
  };

  const handleClone = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/estimates/${id}/clone`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const clone = await res.json();
        setEstimates(prev => [clone, ...prev]);
        toast.success('Estimate cloned');
      }
    } catch (err) {
      toast.error('Failed to clone');
    }
  };

  // Inline status change — optimistic, rolls back on failure.
  const handleStatusChange = async (id, newStatus) => {
    const prev = estimates;
    setEstimates(p => p.map(e => e.estimate_id === id ? { ...e, status: newStatus } : e));
    try {
      const res = await fetch(`${API}/estimates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch {
      setEstimates(prev);
      toast.error('Failed to update status');
    }
  };

  const handleExportPdf = async (id, estNum, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/estimates/${id}/pdf`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${estNum}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      }
    } catch (err) {
      toast.error('Failed to export PDF');
    }
  };

  // Distinct types present in the data — drives the Type filter
  const availableTypes = useMemo(() => {
    const set = new Set();
    estimates.forEach(e => { if (e.estimate_type) set.add(e.estimate_type); });
    return Array.from(set).sort();
  }, [estimates]);

  // Pre-compute searchable haystack per estimate (once per list change)
  const haystacks = useMemo(() => {
    const m = new Map();
    estimates.forEach(e => m.set(e.estimate_id, searchHaystack(e)));
    return m;
  }, [estimates]);

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = estimates.filter(e => {
      const matchSearch = !q || (haystacks.get(e.estimate_id) || '').includes(q);
      const matchStatus = statusFilter === 'all' || e.status === statusFilter;
      const matchType = typeFilter === 'all' || e.estimate_type === typeFilter;
      return matchSearch && matchStatus && matchType;
    });
    const col = SORT_COLUMNS.find(c => c.key === sortKey) || SORT_COLUMNS[0];
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      const va = col.get(a);
      const vb = col.get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [estimates, haystacks, search, statusFilter, typeFilter, sortKey, sortDir]);

  // Clicking a column header toggles sort on that key
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 inline opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 inline text-[#2D6B68]" />
      : <ArrowDown className="w-3 h-3 inline text-[#2D6B68]" />;
  };

  const Th = ({ column, className = '', align = 'left', children }) => (
    <th
      className={`text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-3 font-semibold select-none cursor-pointer hover:text-[#1A1D1C] transition-colors ${className}`}
      style={{ textAlign: align }}
      onClick={() => toggleSort(column)}
      data-testid={`th-${column}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <SortIcon column={column} />
      </span>
    </th>
  );

  const hasActiveFilters = search || statusFilter !== 'all' || typeFilter !== 'all';

  return (
    <div className="animate-fade-in" data-testid="estimates-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Estimates
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">
            {filteredSorted.length} of {estimates.length}
            {hasActiveFilters && filteredSorted.length !== estimates.length ? ' (filtered)' : ' total'}
          </p>
        </div>
        <Button
          data-testid="new-estimate-from-list-btn"
          onClick={() => navigate('/templates')}
          className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-6 py-2.5 font-medium"
        >
          <Plus className="w-4 h-4 mr-2" /> New Estimate
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7A73]" />
          <Input
            data-testid="search-estimates-input"
            placeholder="Search title, #, client, address, phone, notes…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#E6EBE8] text-[#1A1D1C] rounded-md focus:border-[#2D6B68] focus:ring-1 focus:ring-[#2D6B68]"
          />
        </div>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 bg-white border-[#E6EBE8] rounded-md" data-testid="type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {availableTypes.map(t => (
              <SelectItem key={t} value={t}>{typeLabel(t)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white border-[#E6EBE8] rounded-md" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-[#6B7A73]">Sort by</span>
          <Select value={sortKey} onValueChange={setSortKey}>
            <SelectTrigger className="w-40 bg-white border-[#E6EBE8] rounded-md" data-testid="sort-key-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_COLUMNS.map(c => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            className="border-[#E6EBE8] text-[#3A423E] hover:bg-[#F5F6F5]"
            data-testid="sort-direction-btn"
            title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortDir === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-[#E6EBE8] mx-auto mb-4" />
            <p className="text-[#6B7A73] text-sm">
              {hasActiveFilters ? 'No estimates match your filters.' : 'No estimates yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]" data-testid="estimates-table">
              <thead>
                <tr className="bg-[#F9F9F8] border-b border-[#E6EBE8]">
                  <Th column="client">Client</Th>
                  <Th column="estimate_type">Type</Th>
                  <Th column="title" className="hidden md:table-cell">Job</Th>
                  <Th column="estimate_number">Estimate #</Th>
                  <Th column="created_at" className="hidden lg:table-cell">Created</Th>
                  <Th column="updated_at" className="hidden xl:table-cell">Last Edited</Th>
                  <Th column="total" align="right">Total</Th>
                  <Th column="status" align="center">Status</Th>
                  <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E6EBE8]">
                {filteredSorted.map(est => {
                  const cfg = STATUS_CONFIG[est.status] || STATUS_CONFIG.draft;
                  const StatusIcon = cfg.icon;
                  const clientDisplay = clientLastFirst(est.client) || '—';
                  const clientSecondary = est.client?.company && est.client?.name
                    ? est.client.name : '';
                  return (
                    <tr
                      key={est.estimate_id}
                      className="hover:bg-[#F9F9F8] transition-colors cursor-pointer"
                      onClick={() => navigate(`/estimates/${est.estimate_id}`)}
                      data-testid={`estimate-row-${est.estimate_id}`}
                    >
                      <td className="py-3 px-3 max-w-[220px]">
                        <p className="text-sm font-medium text-[#1A1D1C] truncate" title={clientDisplay}>{clientDisplay}</p>
                        {clientSecondary && (
                          <p className="text-[11px] text-[#6B7A73] truncate">{clientSecondary}</p>
                        )}
                        <p className="text-xs text-[#6B7A73] md:hidden truncate mt-0.5">{est.title || '—'}</p>
                      </td>
                      <td className="py-3 px-3">
                        <span className="inline-block text-[11px] font-medium text-[#2D6B68] bg-[#E5F0EE] px-2 py-0.5 rounded whitespace-nowrap">
                          {typeLabel(est.estimate_type)}
                        </span>
                      </td>
                      <td className="py-3 px-3 hidden md:table-cell max-w-[260px]">
                        <p className="text-sm text-[#3A423E] truncate" title={est.title}>{est.title || '—'}</p>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-[#3A423E] font-mono" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                            {est.estimate_number || '—'}
                          </span>
                          {coCounts[est.estimate_id] > 0 && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px] font-semibold text-amber-800 uppercase tracking-wider"
                              data-testid={`co-count-${est.estimate_id}`}
                              title={`${coCounts[est.estimate_id]} change order${coCounts[est.estimate_id] !== 1 ? 's' : ''}`}
                            >
                              <GitBranch className="w-2.5 h-2.5" />
                              {coCounts[est.estimate_id]}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 hidden lg:table-cell">
                        <p className="text-xs text-[#6B7A73]">
                          {est.created_at ? new Date(est.created_at).toLocaleDateString() : '—'}
                        </p>
                      </td>
                      <td className="py-3 px-3 hidden xl:table-cell">
                        <p className="text-xs text-[#6B7A73]">
                          {est.updated_at ? new Date(est.updated_at).toLocaleDateString() : '—'}
                        </p>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className="text-sm font-medium text-[#1A1D1C] whitespace-nowrap" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                          ${calcTotal(est).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={est.status || 'draft'}
                          onValueChange={(v) => handleStatusChange(est.estimate_id, v)}
                        >
                          <SelectTrigger
                            className={`${cfg.color} border rounded-full px-2.5 py-0.5 h-auto text-xs font-medium inline-flex items-center gap-1 w-auto min-w-0 hover:brightness-95 transition-all [&>svg]:opacity-50 [&>svg]:ml-1`}
                            data-testid={`status-trigger-${est.estimate_id}`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            <span>{cfg.label}</span>
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_CONFIG).map(([key, c]) => {
                              const Icon = c.icon;
                              return (
                                <SelectItem key={key} value={key} data-testid={`status-option-${key}-${est.estimate_id}`}>
                                  <span className="inline-flex items-center gap-2">
                                    <Icon className="w-3 h-3" />
                                    {c.label}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => handleClone(est.estimate_id, e)}
                            className="p-1.5 rounded-md text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
                            title="Clone"
                            data-testid={`clone-btn-${est.estimate_id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => handleExportPdf(est.estimate_id, est.estimate_number, e)}
                            className="p-1.5 rounded-md text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
                            title="Export PDF"
                            data-testid={`pdf-btn-${est.estimate_id}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteId(est.estimate_id); }}
                            className="p-1.5 rounded-md text-[#6B7A73] hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete"
                            data-testid={`delete-btn-${est.estimate_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Delete Estimate?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">This action cannot be undone. The estimate will be permanently deleted.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#E6EBE8]" data-testid="cancel-delete-btn">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
